// shared/revert-on-refusal.ts — the PERMISSION-FAIL REVERT FRAME (R-121, 2026-09-30; board R-107 "still owed").
//
// The browser decides an action and draws it at once — on its own page and, through the commit's relay, on every peer page. PHP then
// checks permission (authority from the TARGET ROW) and lazily saves. When PHP REFUSES — permission (403), a stale revision / CAS (409),
// a failed save (5xx), or the request never answers (network) — every page returns to the state before that action. This module is the
// one way a lane does that; it wraps shared/undo-frame.ts (D-049), so a revert undoes only what THIS action wrote (three-way: a newer
// change another action made to the same row wins as a conflict and stays).
//
// ── API (lanes: status / resource / action / inventory-* / destruction chain; Session A2 wires ability / attack / movement / zone-entry /
//    force-move / turn with the same calls) ────────────────────────────────────────────────────────────────────────────────────────────
//
//   classifyRefusal({ status, body, thrown })   → TruthRefusal | null   (null = accepted: a 2xx whose body says ok:true)
//       Reads the uniform PHP refusal body `{ ok:false, reason, error, rev, authoritative }` (truth_refusal.php) and still understands
//       the older bodies (`error:'stale'`, `frame`, a bare status). reason ∈ permission | stale | invalid | unsupported | busy | save | network.
//   refusalFromFrame(frame)                     → TruthRefusal          a peer's `correction` WebSocket frame, read the same way.
//   isFinalRefusal(refusal)                     → boolean               4xx (permission / stale / invalid / unsupported) — never retried.
//
//   const draw = drawWithRevert(targets, () => applyMyFrame(), { redraw, applyAuthoritative });
//       Runs the lane's own mutation inside captureUndo (targets = UndoTarget[]: a holder + key, with `resolve` for a store that a full
//       push may swap). Returns an OptimisticDraw:
//         draw.refuse(refusal)  → UndoReport | null   revert three-way, THEN apply `refusal.authoritative` through the lane's hook, THEN
//                                                     redraw once. Idempotent: a second refusal (the HTTP answer after the peer frame) is null.
//         draw.accept()                              the save landed: the frame is dropped; a later refusal is ignored.
//
//   const ledger = new RevertLedger<Authoritative>();          one per lane, keyed by operation id (bounded).
//       ledger.track(opId, draw)            remember this page's optimistic draw (own OR a peer's relayed provisional).
//       ledger.admit(opId)                  false when the operation is already terminal: a late provisional never draws again.
//       ledger.accept(opId)                 committed — drop the draw.
//       ledger.refuse(opId, refusal)        the HTTP refusal on the initiator.
//       ledger.correction(opId, frame, fallback?)   a `correction` frame: revert a tracked draw (own or relayed), else (nothing drawn
//                                           here) hand the authoritative rows to `fallback`. Terminal either way.
//
// Pure data, no DOM, no fetch: the lane owns its transport, its warning text and its re-projection (the `redraw` hook).
import { captureUndo, defaultUndoIdOf, type IdOf, type UndoFrame, type UndoReport, type UndoTarget } from './undo-frame.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

export type RefusalReason = 'permission' | 'stale' | 'invalid' | 'unsupported' | 'busy' | 'save' | 'network';
const REASONS: readonly RefusalReason[] = ['permission', 'stale', 'invalid', 'unsupported', 'busy', 'save', 'network'];
export interface TruthRefusal {
  ok: false;
  reason: RefusalReason;
  /** HTTP status (0 = no answer / a WebSocket frame). */
  status: number;
  /** The server's message (kept verbatim for the lane's warning). */
  error: string;
  /** The authoritative revision the server holds, when it said. */
  rev: number | null;
  /** The authoritative rows / values for the touched members (lane-shaped), when the server sent them. */
  authoritative: unknown;
  /** The whole answer body, for lane-specific fields. */
  body: Row | null;
}

/** The reason a status / body means, when the body does not name one (the pre-contract endpoints). */
function reasonOf(status: number, body: Row | null, thrown: boolean): RefusalReason {
  const named = typeof body?.reason === 'string' ? body.reason as RefusalReason : null;
  if (named && REASONS.includes(named)) return named;
  const error = String(body?.error ?? '');
  if (thrown || status === 0) return 'network';
  if (status === 401 || status === 403 || error === 'permission_denied') return 'permission';
  if (status === 409 || error === 'stale') return 'stale';
  if (status === 422 && error.startsWith('unsupported')) return 'unsupported';
  if (status === 503) return 'busy';
  if (status >= 400 && status < 500) return 'invalid';
  return 'save';
}

/**
 * The answer to a truth commit: null when ACCEPTED (2xx with `ok: true`), else the refusal. `thrown` = the fetch itself failed (network /
 * timeout / an unreadable answer): a refusal with reason `network` — the save MAY have landed, so a lane that cannot retry idempotently
 * should wait for the committed / correction frame instead of reverting (see `isFinalRefusal`).
 */
export function classifyRefusal(input: { status?: number; body?: unknown; thrown?: unknown }): TruthRefusal | null {
  const status = Number(input.status ?? 0) || 0;
  const body = asRow(input.body);
  const thrown = input.thrown !== undefined && input.thrown !== null;
  if (!thrown && status >= 200 && status < 300 && body?.ok === true) return null;
  const rev = Number.isInteger(body?.rev) ? Number(body!.rev) : null;
  const authoritative = body ? (body.authoritative ?? body.frame ?? null) : null;
  const error = thrown ? (input.thrown instanceof Error ? input.thrown.message : String(input.thrown))
    : String(body?.error ?? (status ? `HTTP ${status}` : 'no answer'));
  return { ok: false, reason: reasonOf(status, body, thrown), status, error, rev, authoritative, body };
}

/** A peer's `correction` frame as a refusal (reason from the frame, else `stale` — the pre-contract frames only corrected CAS losses). */
export function refusalFromFrame(frame: unknown): TruthRefusal {
  const f = asRow(frame) ?? {};
  const named = typeof f.reason === 'string' && REASONS.includes(f.reason as RefusalReason) ? f.reason as RefusalReason : null;
  const rev = Number.isInteger(f.rev) ? Number(f.rev) : null;
  return { ok: false, reason: named ?? (f.reason === 'refused' ? 'permission' : f.reason === 'error' ? 'save' : 'stale'), status: 0,
    error: String(f.why ?? f.reason ?? 'corrected'), rev, authoritative: f.authoritative ?? f.frame ?? null, body: f };
}

/** A refusal that no retry can change (the server answered 4xx): revert now. `busy` / `save` / `network` may be retried first. */
export function isFinalRefusal(refusal: TruthRefusal): boolean {
  return refusal.reason === 'permission' || refusal.reason === 'stale' || refusal.reason === 'invalid' || refusal.reason === 'unsupported';
}

export interface RevertHooks<A = unknown> {
  /** Adopt the server's authoritative rows / values (runs AFTER the three-way revert, only when the refusal carries them). */
  applyAuthoritative?: (authoritative: A, refusal: TruthRefusal) => void;
  /** Re-project once the state is back (the lane's own caster_update / rerender); runs after `applyAuthoritative`. */
  redraw?: (report: UndoReport, refusal: TruthRefusal) => void;
}
export type DrawState = 'pending' | 'accepted' | 'reverted';
export interface OptimisticDraw {
  readonly undo: UndoFrame;
  readonly state: DrawState;
  /** Revert this draw (three-way), apply the authoritative rows, redraw. Null when already settled (idempotent). */
  refuse(refusal: TruthRefusal): UndoReport | null;
  /** The save landed: nothing to revert any more. */
  accept(): void;
}

/** Draw `mutate` inside an undo frame over `targets`; the handle reverts it on a refusal. */
export function drawWithRevert<A = unknown>(targets: readonly UndoTarget[], mutate: () => void, hooks: RevertHooks<A> = {}, idOf: IdOf = defaultUndoIdOf): OptimisticDraw {
  const undo = captureUndo(targets, mutate, idOf);
  let state: DrawState = 'pending';
  return {
    undo,
    get state() { return state; },
    refuse(refusal: TruthRefusal): UndoReport | null {
      if (state !== 'pending') return null;
      state = 'reverted';
      const report = undo.revert();
      if (refusal.authoritative !== null && refusal.authoritative !== undefined && hooks.applyAuthoritative) hooks.applyAuthoritative(refusal.authoritative as A, refusal);
      hooks.redraw?.(report, refusal);
      return report;
    },
    accept(): void { if (state === 'pending') state = 'accepted'; },
  };
}

export type CorrectionOutcome = 'reverted' | 'authoritative' | 'ignored';
/** Per-lane optimistic draws by operation id: the initiator's own and the peers' relayed provisionals, settled once. */
export class RevertLedger<A = unknown> {
  private readonly draws = new Map<string, { draw: Pick<OptimisticDraw, 'accept' | 'refuse'>; at: number }>();
  private readonly terminal = new Map<string, 'accepted' | 'reverted'>();
  /** `limit` bounds the SETTLED ids remembered; a pending draw is never evicted for capacity (that would silently accept an unsettled action
   * and lose its rollback — Codex review) — only once it is older than `maxAgeMs` (its committed / correction frame is long overdue). */
  constructor(private readonly limit = 256, private readonly maxAgeMs = 10 * 60 * 1000, private readonly now: () => number = () => Date.now()) {}
  private key(opId: string): string { return String(opId ?? '').trim().toUpperCase(); }
  private settle(key: string, how: 'accepted' | 'reverted'): void {
    this.draws.delete(key);
    this.terminal.set(key, how);
    if (this.terminal.size > this.limit) this.terminal.delete(this.terminal.keys().next().value!);
  }
  /** False when the operation already settled (a late provisional relay must not draw it again). */
  admit(opId: string): boolean { const k = this.key(opId); return !this.terminal.has(k) && !this.draws.has(k); }
  track(opId: string, draw: Pick<OptimisticDraw, 'accept' | 'refuse'>): void {
    const k = this.key(opId);
    if (this.terminal.has(k)) { if (this.terminal.get(k) === 'reverted') draw.refuse({ ok: false, reason: 'stale', status: 0, error: 'already corrected', rev: null, authoritative: null, body: null }); else draw.accept(); return; }
    const at = this.now();
    for (const [id, held] of this.draws) { if (at - held.at <= this.maxAgeMs) break; this.draws.delete(id); } // insertion order = age order
    this.draws.set(k, { draw, at });
  }
  has(opId: string): boolean { return this.draws.has(this.key(opId)); }
  settled(opId: string): 'accepted' | 'reverted' | null { return this.terminal.get(this.key(opId)) ?? null; }
  accept(opId: string): void { const k = this.key(opId); this.draws.get(k)?.draw.accept(); this.settle(k, 'accepted'); }
  /** The initiator's HTTP refusal. Null when nothing was tracked or it already settled. */
  refuse(opId: string, refusal: TruthRefusal): UndoReport | null {
    const k = this.key(opId);
    const draw = this.draws.get(k)?.draw;
    if (!draw || this.terminal.has(k)) return null;
    this.settle(k, 'reverted');
    return draw.refuse(refusal);
  }
  /**
   * A `correction` frame (initiator or peer). A tracked draw is reverted and the frame's authoritative rows applied through its hooks;
   * with nothing drawn here (the relay never reached this page) `fallback` adopts the authoritative rows. A settled operation is ignored,
   * EXCEPT that an operation this page already accepted still takes the authoritative rows (a correction after the answer wins).
   */
  correction(opId: string, frame: unknown, fallback?: (refusal: TruthRefusal) => void): CorrectionOutcome {
    const k = this.key(opId);
    const refusal = refusalFromFrame(frame);
    const draw = this.draws.get(k)?.draw;
    if (draw && !this.terminal.has(k)) { this.settle(k, 'reverted'); draw.refuse(refusal); return 'reverted'; }
    if (this.terminal.get(k) === 'reverted') return 'ignored';
    this.settle(k, 'reverted');
    if (fallback && refusal.authoritative !== null && refusal.authoritative !== undefined) { fallback(refusal); return 'authoritative'; }
    return 'ignored';
  }
}
